import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ErrorMessage, Field, Form, Formik } from "formik";
import DatePicker from "react-date-picker";
import TimePicker from "react-time-picker";
import "react-time-picker/dist/TimePicker.css";
import "react-clock/dist/Clock.css";
import moment from "moment";
import { setMainLoading, setPageTitle } from "../../Store/reducers/Common";
import { PostAxios, responseCatchFunc } from "../../Services/RequestService";
import { setToastData } from "../../Store/reducers/Toast";
import { ConvertErrorArrayToString } from "../../Services/Common";
import Select from "react-select";

const customMultiSelectStyles = {
    control: (provided) => ({
        ...provided,
        maxHeight: "100px",
        overflowY: "auto",
    }),
    menu: (provided) => ({
        ...provided,
        maxHeight: "200px",
        overflowY: "auto",
    }),
    menuList: (provided) => ({
        ...provided,
        maxHeight: "200px",
        overflowY: "auto",
    }),
};

const AddDivisionRegistrationEvent = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { accessToken, userData } = useSelector((state) => state.user);
    const [orgList, setOrgList] = useState([]);
    const [divList, setDivList] = useState([]);

    useEffect(() => {
        dispatch(setPageTitle("Add Division Registration Event"));
        const promises = [];
        if (userData.role_slug === "super_admin") {
            promises.push(PostAxios(`masters/organization`, { page: 1, perPage: 1000 }, accessToken));
        }
        promises.push(
            PostAxios(
                `masters/division-list`,
                { org_id: userData.role_slug === "super_admin" ? 0 : userData.org_id, page: 1, perPage: 1000 },
                accessToken
            )
        );
        dispatch(setMainLoading(true));
        Promise.all(promises)
            .then((responses) => {
                responses.forEach((res) => {
                    if (res.config.url === "masters/organization" && res.data.status === "1") {
                        setOrgList(res.data.data);
                    }
                    if (res.config.url === "masters/division-list" && res.data.status === "1") {
                        setDivList(res.data.data);
                    }
                });
            })
            .catch((err) => responseCatchFunc(dispatch, err))
            .finally(() => dispatch(setMainLoading(false)));
    }, [accessToken, dispatch, userData]);

    const filteredDivList = useMemo(() => {
        return (orgId) => divList.filter((div) => parseInt(div.org_id) === parseInt(orgId) || parseInt(orgId) === 0);
    }, [divList]);

    const FormikTimePicker = ({ field, form, ...props }) => (
        <TimePicker
            {...props}
            onChange={(val) => form.setFieldValue(field.name, val || "")}
            value={field.value || null}
        />
    );

    const normalizeTimeToMinutes = (time) => {
        if (!time || typeof time !== "string") return null;
        const parts = time.split(":");
        if (parts.length < 2) return null;
        const hours = parseInt(parts[0], 10);
        const minutes = parseInt(parts[1], 10);
        if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
        return hours * 60 + minutes;
    };

    const toApiTime = (time) => {
        if (!time) return time;
        const parts = time.split(":");
        return parts.length === 2 ? `${time}:00` : time;
    };

    if (!["super_admin", "org_admin"].includes(userData.role_slug)) {
        return <div className="p-3">Only Super Admin or Org Admin can create division registration events.</div>;
    }

    return (
        <div className="main-form-sec">
            <Formik
                initialValues={{
                    event_organization: parseInt(userData.org_id) > 0 ? parseInt(userData.org_id) : 0,
                    event_user_div: ["0"],
                    event_title: "",
                    registration_start_date: "",
                    registration_end_date: "",
                    event_start_date: "",
                    event_end_date: "",
                    event_start_time: "09:30",
                    attendence_time: "10:30",
                    event_description: "",
                    event_location: "",
                }}
                validate={(values) => {
                    const errors = {};
                    if (parseInt(values.event_organization) < 1) errors.event_organization = "Organization is required";
                    if (!values.event_user_div || values.event_user_div.length < 1) {
                        errors.event_user_div = "Please select at least one division";
                    }
                    if (!values.event_title) errors.event_title = "Event title is required";
                    if (!values.registration_start_date) errors.registration_start_date = "Registration start date is required";
                    if (!values.registration_end_date) errors.registration_end_date = "Registration end date is required";
                    if (!values.event_start_date) errors.event_start_date = "Attendance start date is required";
                    if (!values.event_end_date) errors.event_end_date = "Attendance end date is required";
                    if (!values.event_start_time) errors.event_start_time = "Daily start time is required";
                    if (!values.attendence_time) errors.attendence_time = "Daily attendance end time is required";
                    if (!values.event_description) errors.event_description = "Description is required";
                    if (!values.event_location) errors.event_location = "Location is required";
                    if (values.registration_start_date && values.registration_end_date && moment(values.registration_end_date).isBefore(values.registration_start_date)) {
                        errors.registration_end_date = "Registration end must be after start";
                    }
                    if (values.event_start_date && values.event_end_date && moment(values.event_end_date).isBefore(values.event_start_date)) {
                        errors.event_end_date = "Attendance end must be after start";
                    }
                    if (values.registration_end_date && values.event_start_date && moment(values.registration_end_date).isAfter(values.event_start_date)) {
                        errors.registration_end_date = "Registration must end before attendance starts";
                    }
                    const startMinutes = normalizeTimeToMinutes(values.event_start_time);
                    const endMinutes = normalizeTimeToMinutes(values.attendence_time);
                    if (startMinutes !== null && endMinutes !== null && endMinutes <= startMinutes) {
                        errors.attendence_time = "Attendance end time must be after start time";
                    }
                    return errors;
                }}
                onSubmit={(values) => {
                    if (!navigator.onLine) {
                        dispatch(setToastData({ message: "Internet connection is required to create an event.", type: "error", showCloseBtn: true }));
                        return;
                    }
                    dispatch(setMainLoading(true));
                    const payload = {
                        ...values,
                        event_user_div: values.event_user_div.includes("0") ? "0" : values.event_user_div.join(","),
                        registration_start_date: moment(values.registration_start_date).format("YYYY-MM-DD"),
                        registration_end_date: moment(values.registration_end_date).format("YYYY-MM-DD"),
                        event_start_date: moment(values.event_start_date).format("YYYY-MM-DD"),
                        event_end_date: moment(values.event_end_date).format("YYYY-MM-DD"),
                        event_start_time: toApiTime(values.event_start_time),
                        attendence_time: toApiTime(values.attendence_time),
                        event_end_time: toApiTime(values.attendence_time),
                    };
                    PostAxios(`event/add-division-registration-event`, payload, accessToken)
                        .then(({ data }) => {
                            if (data.status === "1") {
                                dispatch(setToastData({ message: data.message, type: "success", showCloseBtn: true }));
                                navigate("/events");
                            } else {
                                dispatch(setToastData({
                                    message: Object.values(data.errors || {}).length ? ConvertErrorArrayToString(data.errors) : data.message,
                                    type: "error",
                                    showCloseBtn: true,
                                }));
                            }
                        })
                        .catch((err) => responseCatchFunc(dispatch, err))
                        .finally(() => dispatch(setMainLoading(false)));
                }}
            >
                {({ values, setFieldValue, errors, touched }) => {
                    const divisionOptions = [
                        { label: "All Divisions", value: "0" },
                        ...filteredDivList(values.event_organization).map((div) => ({
                            label: `${div.division_name} (${div.division_number})`,
                            value: String(div.ID),
                        })),
                    ];
                    return (
                        <Form>
                            <div className="form-sec">
                                <div className="form-field-group">
                                    <h4 className="mb-3 w-100">Division Registration Event</h4>
                                    <p className="text-muted">Captains and Division Admins register members during the registration period. A single QR code is used daily during the attendance window.</p>
                                </div>

                                {userData.role_slug === "super_admin" && (
                                    <div className={`form-field-group w-50 ${(errors.event_organization && touched.event_organization) ? 'error-input' : ''}`}>
                                        <div className="w-50">
                                            <label>Organization *</label>
                                        </div>
                                        <div className="w-50">
                                            <label className="select-input-outer select-100">
                                                <Field as="select" name="event_organization" className="select-input" onChange={(e) => {
                                                    setFieldValue("event_organization", parseInt(e.target.value));
                                                    setFieldValue("event_user_div", ["0"]);
                                                }}>
                                                    <option value={0}>Select Organization</option>
                                                    {orgList.map((org) => (
                                                        <option key={org.ID} value={org.ID}>{org.org_name}</option>
                                                    ))}
                                                </Field>
                                            </label>
                                            <ErrorMessage name="event_organization" component="span" className="error-label" />
                                        </div>
                                    </div>
                                )}

                                <div className={`form-field-group w-50 ${(errors.event_user_div && touched.event_user_div) ? 'error-input' : ''}`}>
                                    <div className="w-50">
                                        <label>Divisions *</label>
                                    </div>
                                    <div className="w-50">
                                        <Select
                                            styles={customMultiSelectStyles}
                                            options={divisionOptions}
                                            placeholder="Select divisions"
                                            value={divisionOptions.filter((opt) => values.event_user_div.includes(opt.value))}
                                            onChange={(selectedValues) => {
                                                const selected = selectedValues || [];
                                                if (!selected.length) {
                                                    setFieldValue("event_user_div", []);
                                                    return;
                                                }
                                                if (selected.some((option) => option.value === "0")) {
                                                    setFieldValue("event_user_div", ["0"]);
                                                } else {
                                                    setFieldValue("event_user_div", selected.map((option) => option.value));
                                                }
                                            }}
                                            isSearchable
                                            isMulti
                                            isClearable
                                            menuPortalTarget={document.body}
                                        />
                                        <ErrorMessage name="event_user_div" component="span" className="error-label" />
                                    </div>
                                </div>

                                <div className={`form-field-group w-50 ${(errors.event_title && touched.event_title) ? 'error-input' : ''}`}>
                                    <div className="w-50">
                                        <label>Event Title *</label>
                                    </div>
                                    <div className="w-50 input-area">
                                        <Field name="event_title" className="form-input" placeholder="Event Title" />
                                        <ErrorMessage name="event_title" component="span" className="error-label" />
                                    </div>
                                </div>
                                <div className={`form-field-group w-50 ${(errors.event_description && touched.event_description) ? 'error-input' : ''}`}>
                                    <div className="w-50">
                                        <label>Description *</label>
                                    </div>
                                    <div className="w-50 input-area">
                                        <Field as="textarea" name="event_description" className="form-input form-textarea" rows={5} placeholder="Description" />
                                        <ErrorMessage name="event_description" component="span" className="error-label" />
                                    </div>
                                </div>
                                <div className={`form-field-group w-50 ${(errors.registration_start_date && touched.registration_start_date) ? 'error-input' : ''}`}>
                                    <div className="w-50">
                                        <label>Registration Start Date *</label>
                                    </div>
                                    <div className="w-50">
                                        <div className="date-picker-sec">
                                            <DatePicker
                                                onChange={(val) => setFieldValue("registration_start_date", val)}
                                                clearIcon={null}
                                                minDate={new Date()}
                                                dayPlaceholder="DD"
                                                monthPlaceholder="MM"
                                                yearPlaceholder="YYYY"
                                                value={values.registration_start_date}
                                                className="form-input"
                                                format="dd-MM-y" />
                                        </div>
                                        <ErrorMessage name="registration_start_date" component="span" className="error-label" />
                                    </div>
                                </div>
                                <div className={`form-field-group w-50 ${(errors.registration_end_date && touched.registration_end_date) ? 'error-input' : ''}`}>
                                    <div className="w-50">
                                        <label>Registration End Date *</label>
                                    </div>
                                    <div className="w-50">
                                        <div className="date-picker-sec">
                                            <DatePicker
                                                onChange={(val) => setFieldValue("registration_end_date", val)}
                                                clearIcon={null}
                                                minDate={new Date(values.registration_start_date)}
                                                dayPlaceholder="DD"
                                                monthPlaceholder="MM"
                                                yearPlaceholder="YYYY"
                                                value={values.registration_end_date}
                                                className="form-input"
                                                format="dd-MM-y" />
                                        </div>
                                        <ErrorMessage name="registration_end_date" component="span" className="error-label" />
                                    </div>
                                </div>
                                <div className={`form-field-group w-50 ${(errors.event_start_date && touched.event_start_date) ? 'error-input' : ''}`}>
                                    <div className="w-50">
                                        <label>Attendance Start Date *</label>
                                    </div>
                                    <div className="w-50">
                                        <div className="date-picker-sec">
                                            <DatePicker
                                                onChange={(val) => setFieldValue("event_start_date", val)}
                                                clearIcon={null}
                                                minDate={new Date(values.registration_end_date)}
                                                dayPlaceholder="DD"
                                                monthPlaceholder="MM"
                                                yearPlaceholder="YYYY"
                                                value={values.event_start_date}
                                                className="form-input"
                                                format="dd-MM-y" />
                                        </div>
                                        <ErrorMessage name="event_start_date" component="span" className="error-label" />
                                    </div>
                                </div>
                                <div className={`form-field-group w-50 ${(errors.event_end_date && touched.event_end_date) ? 'error-input' : ''}`}>
                                    <div className="w-50">
                                        <label>Attendance End Date *</label>
                                    </div>
                                    <div className="w-50">
                                        <div className="date-picker-sec">
                                            <DatePicker
                                                onChange={(val) => setFieldValue("event_end_date", val)}
                                                clearIcon={null}
                                                minDate={new Date(values.event_start_date)}
                                                dayPlaceholder="DD"
                                                monthPlaceholder="MM"
                                                yearPlaceholder="YYYY"
                                                value={values.event_end_date}
                                                className="form-input"
                                                format="dd-MM-y" />
                                        </div>
                                        <ErrorMessage name="event_end_date" component="span" className="error-label" />
                                    </div>
                                </div>

                                <div className={`form-field-group w-50 ${(errors.event_start_time && touched.event_start_time) ? 'error-input' : ''}`}>
                                    <div className="w-50">
                                        <label>Daily Attendance Start Time *</label>
                                    </div>
                                    <div className="w-50">
                                        <Field name="event_start_time" component={FormikTimePicker}
                                            className="form-input"
                                            placeholder="Start Time"
                                            id={`event_start_time`}
                                            clockIcon={null}
                                            disableClock={true}
                                            clearIcon={null}
                                            hourPlaceholder="HH"
                                            minutePlaceholder="mm"
                                        />
                                        <ErrorMessage name="event_start_time" component="span" className="error-label" />
                                    </div>
                                </div>
                                <div className={`form-field-group w-50 ${(errors.attendence_time && touched.attendence_time) ? 'error-input' : ''}`}>
                                    <div className="w-50">
                                        <label>Daily Attendance End Time *</label>
                                    </div>
                                    <div className="w-50">
                                        <Field name="attendence_time" component={FormikTimePicker}
                                            className="form-input"
                                            placeholder="End Time"
                                            id={`event_end_time`}
                                            clockIcon={null}
                                            disableClock={true}
                                            clearIcon={null}
                                            hourPlaceholder="HH"
                                            minutePlaceholder="mm"
                                        />
                                        <ErrorMessage name="attendence_time" component="span" className="error-label" />
                                    </div>
                                </div>
                                <div className={`form-field-group w-50 ${(errors.event_location && touched.event_location) ? 'error-input' : ''}`}>
                                    <div className="w-50">
                                        <label>Location *</label>
                                    </div>
                                    <div className="w-50 input-area">
                                        <Field name="event_location" className="form-input" placeholder="Location" />
                                        <ErrorMessage name="event_location" component="span" className="error-label" />
                                    </div>
                                </div>


                                <div className="btm-right-btn justify-content-center my-3">
                                    <button type="submit" className="btn btn-blue">Create Event</button>
                                    <button type="button" className="btn-blue btn ms-2" onClick={() => navigate("/events")}>Cancel</button>
                                </div>
                            </div>
                        </Form>
                    );
                }}
            </Formik>
        </div>
    );
};

export default AddDivisionRegistrationEvent;
